/**
 * First-party anonymous visitor id (`gp_vid`).
 *
 * A stable, opaque, non-PII id for a browser, persisted in a cookie scoped to
 * the registrable domain the `app.`/`www.`/apex hosts share (like `gp_theme` /
 * `gp_li_fat_id`), so the same id is visible across the origin hop. It is the
 * **first-party visitor id** the marketing-tech and experimentation docs
 * anticipate (see docs/martech.md "server-side visitor IDs" and
 * docs/experimentation.md): today it seeds deterministic experiment bucketing,
 * and it is the foundation a unique-visitor count or a first-party
 * landing→signup join would build on. Lives in its own module rather than inside
 * the experiment engine because its purpose outlives any one experiment.
 *
 * Scope note: this is a *client-set* id. The fuller first-party model martech.md
 * sketches wants a *server-issued* id; this is the same concept one step earlier,
 * and a server-set id can later replace or augment it here without touching the
 * callers.
 */

import { sharedCookieDomain } from './cookie-domain.ts';

const VID_COOKIE = 'gp_vid';
const VID_MAX_AGE_SECONDS = 365 * 24 * 60 * 60;

/**
 * The visitor's id, minting and persisting one on first call if absent. Sticky
 * for a year. Best-effort: if the cookie can't be written (sandboxed context,
 * quota) the caller still gets a stable id for this page's lifetime; only its
 * persistence across reloads is lost.
 *
 * Callers decide *when* a visitor is cookied — e.g. the experiment engine only
 * calls this once an experiment is actually enrolling, so a visitor who never
 * meets one is never cookied.
 */
export function getVisitorId(): string {
  const existing = readVisitorId();
  if (existing) return existing;
  const minted = generateVid();
  writeVisitorId(minted);
  return minted;
}

function readVisitorId(): string | null {
  try {
    const prefix = `${VID_COOKIE}=`;
    const raw = document.cookie
      .split(';')
      .map((c) => c.trim())
      .find((c) => c.startsWith(prefix))
      ?.slice(prefix.length);
    if (!raw) return null;
    const value = decodeURIComponent(raw);
    return value || null;
  } catch {
    return null;
  }
}

function writeVisitorId(vid: string): void {
  try {
    const parts = [
      `${VID_COOKIE}=${encodeURIComponent(vid)}`,
      'Path=/',
      `Max-Age=${VID_MAX_AGE_SECONDS}`,
      'SameSite=Lax',
    ];
    const domain = sharedCookieDomain();
    if (domain) parts.push(`Domain=${domain}`);
    if (window.location.protocol === 'https:') parts.push('Secure');
    document.cookie = parts.join('; ');
  } catch {
    // Best-effort: a cookie write failure must never break the page.
  }
}

// An opaque, non-PII id. `crypto.randomUUID` needs a secure context (https or
// localhost), which production and local dev both satisfy; the random fallback
// covers anything else (the id only needs to be well-distributed, not
// cryptographically strong — consumers hash it).
function generateVid(): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
  } catch {
    // fall through to the non-crypto id
  }
  return `${Math.random().toString(36).slice(2)}${Math.random().toString(36).slice(2)}`;
}
